export enum JOURNEY_SCHEMA_VERSION {
  /**
   * Version 4
   * Introduced in {TODO: Add date}
   */
  V4 = 'v4'
}

export enum JOURNEY_TYPE {
  /**
   * Regular journey
   */
  REGULAR = 'regular',
  /** Journey that launches other journeys */
  LAUNCHER = 'launcher',
  /**
   * Post-qualification journey
   *
   * Currently, this is a regular journey with context parameters,
   * which are required to be filled with another journey.
   *
   * @note: This type is not implemented yet.
   */
  POST_QUALIFICATION = 'post-qualification'
}

export enum JOURNEY_ACCESS_MODE {
  PUBLIC = 'public',
  PRIVATE = 'private'
}

export enum JOURNEY_STATUS {
  PUBLISHED = 'published',
  DRAFT = 'draft'
}

export enum JOURNEY_EMBED_MODE {
  /**
   * Indicates the Journey is embedded in a full-screen,
   * although that might be either in an iframe (i.e. within a dialog) or not.
   */
  FULL_SCREEN = 'full-screen',
  /**
   * Indicates the Journey is embedded in a page, which certainly means
   * it's in an iframe.
   */
  INLINE = 'inline'
}

export enum JOURNEY_PURPOSE {
  /** Refers to {@link RUNTIME_ENTITY.ORDER} */
  SELL_360 = 'sell-360-products',
  /** Refers to both {@link RUNTIME_ENTITY.ORDER} and {@link RUNTIME_ENTITY.OPPORTUNITY} */
  REQUEST_360 = 'request-360-products',
  /** Refers to {@link RUNTIME_ENTITY.OPPORTUNITY} */
  GENERAL_REQUESTS = 'general-requests',
  /** Refers to 'none' or empty runtime entities */
  OTHER = 'other'
}

export enum RUNTIME_ENTITY {
  /** Maps to {@link JOURNEY_PURPOSE.SELL_360} */
  ORDER = 'ORDER',
  /** Maps to {@link JOURNEY_PURPOSE.GENERAL_REQUESTS} */
  OPPORTUNITY = 'OPPORTUNITY'
}

export enum BLOCK_TYPE {
  AUTH = 'auth',
  GROUP = 'group',

  // Stateless
  PARAGRAPH = 'paragraph',

  // Basic inputs
  TEXT_INPUT = 'text-input',
  NUMBER_INPUT = 'number-input',
  DATE_INPUT = 'date-input',
  BINARY_INPUT = 'binary-input',
  SINGLE_CHOICE = 'single-choice',
  MULTIPLE_CHOICE = 'multiple-choice',

  PERSONAL_INFORMATION = 'personal-information',
  CONTACT = 'contact',
  ACCOUNT = 'account',
  ADDRESS = 'address',
  SIGNATURE = 'signature',
  FILE_UPLOAD = 'file-upload',
  AVAILABILITY_CHECK = 'availability-check',

  PRODUCTS = 'products',
  PRODUCT_CATEGORIES = 'product-categories',
  PAYMENT_METHOD = 'payment-method',
  SHOPPING_CART = 'shopping-cart',
  INPUT_CALCULATOR = 'input-calculator',

  IMAGE = 'image',
  ACTION_BAR = 'action-bar',
  HYPERLINK = 'hyperlink',
  CONSENTS = 'consents',
  SUMMARY = 'summary',
  SUCCESS_MESSAGE = 'success-message',
  PDF_GENERATOR = 'pdf-generator',

  CUSTOM = 'custom',
  PREVIOUS_PROVIDER = 'previous-provider',
  PV_ROOFTOP = 'pv-rooftop',
  METER_READING = 'meter-reading',
  ENTITY_ATTRIBUTE = 'entity-attribute',
  ENTITY_FINDER = 'entity-finder',
  ATTRIBUTE_EDITOR = 'attribute-editor',

  APP = 'app'
}

export enum REFERENCED_TYPE {
  /** Allows referencing a step */
  STEP = 'step',
  /** Allows referencing a block */
  BLOCK = 'block',
  /** Allows referencing a context parameter */
  CONTEXT_PARAMETER = 'context-parameter'
}

export enum CONTEXT_PARAMETER_TYPE {
  TEXT = 'text',
  ACCOUNT = 'account-entity',
  CONTACT = 'contact-entity',
  CONTRACT = 'contract-entity',
  OPPORTUNITY = 'opportunity-entity',
  ORDER = 'order-entity',
  REQUEST = 'request'
}

export enum LANGUAGE {
  DE = 'de',
  FR = 'fr',
  EN = 'en'
}

export enum LOGIC_ACTION {
  /**
   * Jumps to a specific Step
   *
   * Allows the user to specify to which Step they should jump to. Also allows the user
   * to control WHEN this should happen.
   */
  JUMP_TO_STEP = 'jump-to-step',
  /**
   * Skips a Step
   *
   * Allows skipping one or multiple Steps, regardless of their order.
   */
  SKIP_STEP = 'skip-step',
  /**
   * Determines whether a block should be displayed
   *
   * Allows the user to control the visibility of a given block, even if
   * that block was marked as required.
   *
   * If a required block is set to be hidden, the restriction is going to be lifted.
   */
  DISPLAY_CONDITION = 'display-condition',
  /**
   * Allows injecting data from one block to another.
   *
   * Depending on the case, it might also transform data and disable fields on the
   * destination block.
   */
  DATA_INJECTION = 'data-injection'
}

export enum LOGIC_OPERATOR {
  EXISTS = 'exists',
  EQUAL = 'equal',
  NOT_EQUAL = 'not-equal',
  GREATER_THAN = 'greater-than',
  GREATER_THAN_OR_EQUAL = 'greater-than-or-equal',
  LESS_THAN = 'less-than',
  LESS_THAN_OR_EQUAL = 'less-than-or-equal',
  /**
   * The fact contains at least one of the selected values
   */
  CONTAINS_EITHER = 'contains-either',
  /**
   * The fact contains all of the selected values, may contain more
   */
  CONTAINS = 'contains',
  /**
   * The does not contain any of the select values
   */
  DOES_NOT_CONTAIN = 'does-not-contain',
  STARTS_WITH = 'starts-with',
  ENDS_WITH = 'ends-with',
  IS_EMPTY = 'is-empty',
  IS_NOT_EMPTY = 'is-not-empty',
  IN = 'in',
  NOT_IN = 'not-in',
  BETWEEN = 'between',
  NOT_BETWEEN = 'not-between',
  MATCHES = 'matches',
  NOT_MATCHES = 'not-matches',
  /**
   * Considered a special operator, the function operator has no specific implementation but rather
   * allows different parts of the application (i.e. Blocks) to express custom logic that can be used
   * to assert clauses.
   *
   * This is useful to perform custom logic that is not covered by the standard operators and does not directly
   * concern the logics engine. Currently, this is used by the AvailabilityCheck block to perform async API calls
   * that check the availability of a product.
   *
   * Idea: we might allow Custom Apps to define their own functions as well.
   */
  CUSTOM_FUNCTION = 'custom-function'
}

export enum LAYOUT_TYPE {
  STACK = 'stack',
  TWO_COLUMN = 'two-column',
  GRID = 'grid'
}

export enum JOURNEY_SAVING_PROGRESS_MODE {
  AUTO = 'auto',
  NONE = 'none',
  /** @deprecated not yet implemented */
  LOCAL = 'local',
  /** @deprecated not yet implemented */
  REMOTE = 'remote'
}

export enum COUNTRY_CODE {
  Germany = 'DE',
  Austria = 'AT',
  Switzerland = 'CH',
  Luxembourg = 'LU'
}

export enum UNITS {
  m = 'm',
  m2 = 'm²',
  'cubic-meter' = 'm³',
  'cubic-meter-h' = 'm³/h',
  l = 'l',
  ls = 'l/s',
  a = 'A',
  kva = 'kVA',
  w = 'W',
  kw = 'kW',
  kwh = 'kWh',
  wp = 'Wp',
  kwp = 'kWp',
  rm = 'rm',
  t = 't',
  kg = 'kg',
  '°' = '°',
  hz = 'Hz',
  kvar = 'kVAr',
  kv = 'kV',
  '1/min' = '1/min',
  mh = 'mH',
  ka = 'kA',
  'kva/s' = 'kVA/s',
  noUnit = 'noUnit',

  // These units are from Meter Reading Units
  wh = 'Wh',
  kvarh = 'kvarh',
  mw = 'mW',
  mwh = 'mWh',
  unit = 'unit',
  hour = 'hour',
  day = 'day',
  month = 'month',
  year = 'year',
  percentage = 'percentage'
}
